import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import {
  currentLocalState,
  getPreviousWorkspaceState,
  WorkspaceProvider,
} from '@sales-app/context/workspace/workspace.provider'
import { WorkspaceInitialState } from '@sales-app/context/workspace/workspace.state'
import { WorkspaceState } from '@sales-app/context/workspace/workspace.type'
import { SalesRouter } from '@sales-app/SalesRouter'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter, useLocation } from 'react-router-dom'

import { AlertPopup, ErrorBoundary } from '@/components'
import { WorkspaceCallback } from '@/components/WorkspaceCallback'
import {
  AlertContextProvider,
  FeatureFlagsProvider,
  FiltersContextState,
  PlannerContextProvider,
  UserContextProvider,
} from '@/context'
import { useTailwindHeight } from '@/hooks'

import { queryClient } from './api/queryClient'
import { CommunicationsRouter } from './CommunicationsRouter'
import { FiltersContextProvider } from './context/filters/filters'
import { currentSupportFiltersKey } from './context/filters/filters.data'
import { getPreviousSavedFilters } from './context/filters/filters.type'
import { SettingsRouter } from './SettingsRouter'
import { SupportRouter } from './SupportRouter'
import { Layout, Scripts } from './views'

const cache = createCache({
  key: 'mission-control',
  container: document.getElementById('emotion-styles') as any,
})

function AppContent({
  initialState,
  intialSupportFilters,
}: {
  initialState: WorkspaceState
  intialSupportFilters: FiltersContextState | undefined
}) {
  const location = useLocation()
  const isWorkspaceCallback = location.pathname === '/auth/workspace-callback'

  return (
    <FiltersContextProvider value={intialSupportFilters}>
      <UserContextProvider>
        <PlannerContextProvider>
          <Scripts />
          <div id="modal-root" />
          <div id="panel-root" />
          <div id="tooltip-root" />
          <AlertPopup />
          {isWorkspaceCallback ? (
            <WorkspaceCallback />
          ) : (
            <Layout>
              <CommunicationsRouter />
              <SettingsRouter />
              <SupportRouter />
              <WorkspaceProvider value={initialState}>
                <SalesRouter />
              </WorkspaceProvider>
            </Layout>
          )}
        </PlannerContextProvider>
      </UserContextProvider>
    </FiltersContextProvider>
  )
}

function App() {
  const prevState = localStorage.getItem(currentLocalState)
  const previousSupportFiltersJSON = localStorage.getItem(currentSupportFiltersKey)

  let initialState = WorkspaceInitialState
  let intialSupportFilters: FiltersContextState | undefined = undefined
  if (prevState) {
    const previousWorkspace = JSON.parse(prevState) as WorkspaceState
    initialState = getPreviousWorkspaceState(previousWorkspace)
  }

  if (previousSupportFiltersJSON) {
    const previousSupportFilters = JSON.parse(previousSupportFiltersJSON) as FiltersContextState
    intialSupportFilters = getPreviousSavedFilters(previousSupportFilters)
  }

  useTailwindHeight()

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <CacheProvider value={cache}>
          <FeatureFlagsProvider>
            <AlertContextProvider>
              <BrowserRouter>
                <AppContent initialState={initialState} intialSupportFilters={intialSupportFilters} />
              </BrowserRouter>
            </AlertContextProvider>
          </FeatureFlagsProvider>
        </CacheProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
